import { Tier, Family } from '../config';
import { getElementDef } from '../data/elements';
import { Grid } from './Grid';

/**
 * Detects game-over and deadlock conditions.
 * Game over: grid full + no merges.
 * Deadlock: grid not full + no merges (needs rescue spawn).
 */
export class GameOverDetector {
  private grid: Grid;

  constructor(grid: Grid) {
    this.grid = grid;
  }

  /**
   * Game is over when no valid merges exist anywhere on the board,
   * regardless of whether the grid is full. No rescue spawns —
   * if you can't merge, the run is over.
   */
  isGameOver(): boolean {
    const elements = this.grid.getAll();
    if (elements.length === 0) return false;
    return !this.hasAnyValidMerge();
  }

  /**
   * Check if any valid merge exists on the board.
   * Standard: any defId appears 2+ times with tier < Eternal.
   * Wild: any wild element + any non-wild element at same tier < Eternal.
   */
  hasAnyValidMerge(): boolean {
    const elements = this.grid.getAll();
    const defCounts = new Map<string, number>();
    const tierElements = new Map<number, number>(); // tier -> count of non-wild elements
    let hasWild = false;
    let wildTier = 0;

    for (const el of elements) {
      const def = getElementDef(el.defId);
      if (!def || def.tier >= Tier.Eternal) continue;

      // Track wild elements
      if (def.family === Family.Wild) {
        hasWild = true;
        wildTier = def.tier;
        continue;
      }

      // Standard pair detection
      const count = (defCounts.get(el.defId) ?? 0) + 1;
      defCounts.set(el.defId, count);
      if (count >= 2) return true;

      // Track non-wild elements by tier for wild merge check
      tierElements.set(def.tier, (tierElements.get(def.tier) ?? 0) + 1);
    }

    // Wild merge check: any non-wild element at same tier as wild
    if (hasWild && (tierElements.get(wildTier) ?? 0) > 0) {
      return true;
    }

    return false;
  }
}
